import { useEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import type { GeoPointValue } from '../types';

export type NativeMapMarker = {
  id: string;
  coordinate: GeoPointValue;
  title?: string;
  label?: string;
  labelClassName?: string;
  labelColor?: string;
  iconUrl?: string;
  iconWidth?: number;
  iconHeight?: number;
  anchorX?: number;
  anchorY?: number;
  interactive?: boolean;
  zIndex?: number;
};

export type NativeMapRoute = {
  id?: string;
  path: GeoPointValue[];
  color: string;
  opacity?: number;
  weight?: number;
  zIndex?: number;
  dashed?: boolean;
};

export type NativeMapPadding = number | { top: number; right: number; bottom: number; left: number };
export type NativeMapCamera = { center: GeoPointValue; zoom: number };

/** Observe user gestures, not camera property events also emitted by fit/pan/zoom. */
export function bindMapCameraInteraction(instance: Pick<google.maps.Map, 'addListener'>, element: HTMLElement, onInteraction: () => void): () => void {
  const listeners = [instance.addListener('dragstart', onInteraction), instance.addListener('dblclick', onInteraction)];
  const onWheel = (event: WheelEvent) => { if (event.deltaX || event.deltaY) onInteraction(); };
  const onKey = (event: KeyboardEvent) => {
    const target = event.target as Element | null;
    if (target?.closest?.('input,textarea,select,button,[role="button"],[contenteditable="true"]')) return;
    if (['+', '-', '=', '_', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)) onInteraction();
  };
  let pinch: { distance: number; x: number; y: number } | null = null;
  const touchPair = (event: TouchEvent) => {
    if (event.touches.length < 2) return null;
    const [a, b] = [event.touches[0], event.touches[1]];
    return { distance: Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY), x: (a.clientX + b.clientX) / 2, y: (a.clientY + b.clientY) / 2 };
  };
  const onTouchStart = (event: TouchEvent) => { pinch = touchPair(event); };
  const onTouchMove = (event: TouchEvent) => {
    const next = touchPair(event);
    if (pinch && next && (Math.abs(next.distance - pinch.distance) > 3 || Math.hypot(next.x - pinch.x, next.y - pinch.y) > 3)) {
      onInteraction();
      pinch = next;
    } else if (!next) pinch = null;
  };
  const onTouchEnd = (event: TouchEvent) => { pinch = touchPair(event); };
  element.addEventListener('wheel', onWheel, { capture: true, passive: true });
  element.addEventListener('keydown', onKey, true);
  element.addEventListener('touchstart', onTouchStart, { capture: true, passive: true });
  element.addEventListener('touchmove', onTouchMove, { capture: true, passive: true });
  element.addEventListener('touchend', onTouchEnd, { capture: true, passive: true });
  element.addEventListener('touchcancel', onTouchEnd, { capture: true, passive: true });
  return () => {
    listeners.forEach((listener) => listener.remove());
    element.removeEventListener('wheel', onWheel, true);
    element.removeEventListener('keydown', onKey, true);
    element.removeEventListener('touchstart', onTouchStart, true);
    element.removeEventListener('touchmove', onTouchMove, true);
    element.removeEventListener('touchend', onTouchEnd, true);
    element.removeEventListener('touchcancel', onTouchEnd, true);
  };
}

export function createNativeCameraMessages(mapId: string, coordinate: GeoPointValue, zoom: number | undefined, supportsCamera: boolean): object[] {
  const nextZoom = Number.isFinite(zoom) ? zoom : undefined;
  if (supportsCamera) return [{ type: 'tragenda-map-camera', mapId, coordinate, ...(nextZoom !== undefined ? { zoom: nextZoom } : {}) }];
  return [
    ...(nextZoom !== undefined ? [{ type: 'tragenda-map-zoom', mapId, zoom: nextZoom }] : []),
    { type: 'tragenda-map-pan', mapId, coordinate },
  ];
}

export type NativeMapState = {
  center: GeoPointValue;
  zoom: number;
  mapTypeId?: 'roadmap' | 'satellite' | 'hybrid';
  markers: NativeMapMarker[];
  routes: NativeMapRoute[];
};
export type NativeGeometryPatch = {
  upsertMarkers: NativeMapMarker[];
  removeMarkerIds: string[];
  upsertRoutes: { key: string; route: NativeMapRoute }[];
  removeRouteIds: string[];
  mapTypeId?: NativeMapState['mapTypeId'];
};

/** React geometry is immutable: unchanged paths retain their array identity. */
export function createNativeGeometryPatch(previous: NativeMapState, next: NativeMapState): NativeGeometryPatch | null {
  const previousMarkers = new Map(previous.markers.map((marker) => [marker.id, marker]));
  const previousRoutes = new Map(previous.routes.map((route, index) => [route.id === undefined ? `index:${index}` : `id:${route.id}`, route]));
  const patch: NativeGeometryPatch = { upsertMarkers: [], removeMarkerIds: [], upsertRoutes: [], removeRouteIds: [] };
  for (const marker of next.markers) {
    const old = previousMarkers.get(marker.id);
    if (!old || old.coordinate.lat !== marker.coordinate.lat || old.coordinate.lng !== marker.coordinate.lng
      || old.title !== marker.title || old.label !== marker.label || old.labelClassName !== marker.labelClassName || old.labelColor !== marker.labelColor
      || old.iconUrl !== marker.iconUrl || old.iconWidth !== marker.iconWidth || old.iconHeight !== marker.iconHeight
      || old.anchorX !== marker.anchorX || old.anchorY !== marker.anchorY || old.interactive !== marker.interactive || old.zIndex !== marker.zIndex) {
      patch.upsertMarkers.push(marker);
    }
    previousMarkers.delete(marker.id);
  }
  for (const [index, route] of next.routes.entries()) {
    const key = route.id === undefined ? `index:${index}` : `id:${route.id}`;
    const old = previousRoutes.get(key);
    // Never stringify/traverse untouched long routes on every playback frame.
    if (!old || old.path !== route.path || old.color !== route.color || old.opacity !== route.opacity
      || old.weight !== route.weight || old.zIndex !== route.zIndex || old.dashed !== route.dashed) {
      patch.upsertRoutes.push({ key, route });
    }
    previousRoutes.delete(key);
  }
  patch.removeMarkerIds = [...previousMarkers.keys()];
  patch.removeRouteIds = [...previousRoutes.keys()];
  if ((previous.mapTypeId ?? 'roadmap') !== (next.mapTypeId ?? 'roadmap')) patch.mapTypeId = next.mapTypeId ?? 'roadmap';
  return patch.upsertMarkers.length || patch.removeMarkerIds.length || patch.upsertRoutes.length || patch.removeRouteIds.length || patch.mapTypeId ? patch : null;
}

export function createNativeMapUpdate(mapId: string, state: NativeMapState, previous: NativeMapState | null, supportsPatches: boolean) {
  if (!supportsPatches || !previous) return { type: 'tragenda-map-state' as const, mapId, state };
  const patch = createNativeGeometryPatch(previous, state);
  return patch ? { type: 'tragenda-map-geometry-patch' as const, mapId, patch } : null;
}

export interface MapSurfaceController {
  fitPoints(points: GeoPointValue[], padding?: NativeMapPadding): void;
  panTo(coordinate: GeoPointValue): void;
  setZoom(zoom: number): void;
  animateCamera?(coordinate: GeoPointValue, zoom?: number): void;
  cancelCameraAnimation?(): void;
  getCamera?(): NativeMapCamera | null;
  setPerspective?(enabled: boolean, heading?: number): void;
}

interface NativeMapSurfaceProps {
  id: string;
  center: GeoPointValue;
  zoom: number;
  mapTypeId?: 'roadmap' | 'satellite' | 'hybrid';
  markers: NativeMapMarker[];
  routes: NativeMapRoute[];
  onController(controller: MapSurfaceController | null): void;
  onMarkerClick?(id: string): void;
  onCameraInteraction?(): void;
  onPerspectiveAvailable?(available: boolean): void;
  onError?(message: string): void;
  className?: string;
}

const frameOrigin = 'https://tragenda.app';

export default function NativeMapSurface({ id, center, zoom, mapTypeId = 'roadmap', markers, routes, onController, onMarkerClick, onCameraInteraction, onPerspectiveAvailable, onError, className }: NativeMapSurfaceProps) {
  const { actualTheme } = useTheme();
  const { t } = useTranslation();
  const appearanceRef = useRef(actualTheme);
  appearanceRef.current = actualTheme;
  const frame = useRef<HTMLIFrameElement | null>(null);
  const ready = useRef(false);
  const supportsGeometryPatches = useRef(false);
  const supportsCamera = useRef(false);
  const supportsPerspective = useRef(false);
  const currentCamera = useRef<NativeMapCamera | null>(null);
  const lastSentState = useRef<NativeMapState | null>(null);
  const pending = useRef<object[]>([]);
  const [frameKey] = useState(() => encodeURIComponent(id));
  const state = useMemo(() => ({ center, zoom, mapTypeId, markers, routes }), [center, mapTypeId, markers, routes, zoom]);
  const stateRef = useRef(state);
  stateRef.current = state;
  const onControllerRef = useRef(onController);
  const onMarkerClickRef = useRef(onMarkerClick);
  const onCameraInteractionRef = useRef(onCameraInteraction);
  const onPerspectiveAvailableRef = useRef(onPerspectiveAvailable);
  const onErrorRef = useRef(onError);
  onControllerRef.current = onController;
  onMarkerClickRef.current = onMarkerClick;
  onCameraInteractionRef.current = onCameraInteraction;
  onPerspectiveAvailableRef.current = onPerspectiveAvailable;
  onErrorRef.current = onError;

  const send = (message: object) => {
    if (!ready.current || !frame.current?.contentWindow) {
      pending.current.push(message);
      return;
    }
    frame.current.contentWindow.postMessage(message, '*');
  };

  const cancelCameraAnimation = () => {
    pending.current = pending.current.filter((message) => (message as { type?: string }).type !== 'tragenda-map-camera');
    // Old hosted frames ignore this optional command; they have no custom RAF
    // animation to cancel. Keep their existing pan/zoom protocol intact.
    if (ready.current) frame.current?.contentWindow?.postMessage({ type: 'tragenda-map-camera-cancel', mapId: id }, '*');
  };

  const controller = useMemo<MapSurfaceController>(() => ({
    fitPoints: (points, padding = 58) => { cancelCameraAnimation(); send({ type: 'tragenda-map-fit', mapId: id, points, padding }); },
    panTo: (coordinate) => { cancelCameraAnimation(); send({ type: 'tragenda-map-pan', mapId: id, coordinate }); },
    setZoom: (nextZoom) => { cancelCameraAnimation(); send({ type: 'tragenda-map-zoom', mapId: id, zoom: nextZoom }); },
    // Before ready, retain the semantic command so cancellation can remove it.
    // Negotiate a legacy fallback only when the frame's capabilities are known.
    animateCamera: (coordinate, nextZoom) => createNativeCameraMessages(id, coordinate, nextZoom, !ready.current || supportsCamera.current).forEach(send),
    cancelCameraAnimation,
    getCamera: () => currentCamera.current ? { center: { ...currentCamera.current.center }, zoom: currentCamera.current.zoom } : null,
    setPerspective: (enabled, heading) => {
      if (supportsPerspective.current) send({ type: 'tragenda-map-perspective', mapId: id, enabled, ...(Number.isFinite(heading) ? { heading } : {}) });
    },
  // The controller intentionally stays stable for this surface id.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [id]);

  useEffect(() => {
    onControllerRef.current(controller);
    const onVisibility = () => { if (document.hidden) controller.cancelCameraAnimation?.(); };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      controller.cancelCameraAnimation?.();
      document.removeEventListener('visibilitychange', onVisibility);
      onControllerRef.current(null);
    };
  }, [controller]);

  useEffect(() => {
    const receive = (event: MessageEvent) => {
      if (event.origin !== frameOrigin || event.source !== frame.current?.contentWindow || event.data?.mapId !== id) return;
      if (event.data.type === 'tragenda-map-ready') {
        ready.current = true;
        supportsGeometryPatches.current = event.data.capabilities?.geometryPatchV1 === true;
        supportsCamera.current = event.data.capabilities?.cameraV1 === true;
        supportsPerspective.current = event.data.capabilities?.perspectiveV1 === true;
        onPerspectiveAvailableRef.current?.(supportsPerspective.current && event.data.capabilities?.supportsTilt === true);
        currentCamera.current = null;
        frame.current?.contentWindow?.postMessage({ type: 'tragenda-map-appearance', mapId: id, theme: appearanceRef.current }, '*');
        frame.current?.contentWindow?.postMessage({ type: 'tragenda-map-state', mapId: id, state: stateRef.current }, '*');
        lastSentState.current = stateRef.current;
        pending.current.splice(0).forEach((message) => {
          const camera = message as { type?: string; coordinate: GeoPointValue; zoom?: number };
          const messages = camera.type === 'tragenda-map-camera'
            ? createNativeCameraMessages(id, camera.coordinate, camera.zoom, supportsCamera.current)
            : [message];
          messages.forEach((entry) => frame.current?.contentWindow?.postMessage(entry, '*'));
        });
      }
      if (event.data.type === 'tragenda-map-marker-click' && typeof event.data.markerId === 'string') onMarkerClickRef.current?.(event.data.markerId);
      if (event.data.type === 'tragenda-map-camera-interaction') {
        controller.cancelCameraAnimation?.();
        onCameraInteractionRef.current?.();
      }
      if (event.data.type === 'tragenda-map-capabilities') {
        supportsPerspective.current = event.data.capabilities?.perspectiveV1 === true;
        onPerspectiveAvailableRef.current?.(supportsPerspective.current && event.data.capabilities?.supportsTilt === true);
      }
      if (event.data.type === 'tragenda-map-camera-changed') {
        const camera = event.data.camera;
        if (Number.isFinite(camera?.center?.lat) && Math.abs(camera.center.lat) <= 90 && Number.isFinite(camera?.center?.lng) && Math.abs(camera.center.lng) <= 180 && Number.isFinite(camera?.zoom)) {
          currentCamera.current = { center: { lat: camera.center.lat, lng: camera.center.lng }, zoom: camera.zoom };
        }
      }
      if (event.data.type === 'tragenda-map-error') onErrorRef.current?.(typeof event.data.message === 'string' ? event.data.message : 'Map could not load.');
    };
    window.addEventListener('message', receive);
    return () => window.removeEventListener('message', receive);
  }, [controller, id]);

  useEffect(() => {
    if (ready.current) frame.current?.contentWindow?.postMessage({ type: 'tragenda-map-appearance', mapId: id, theme: actualTheme }, '*');
  }, [actualTheme, id]);

  useEffect(() => {
    if (!ready.current) return;
    // Older hosted frames still receive the complete protocol they understand.
    const message = createNativeMapUpdate(id, state, lastSentState.current, supportsGeometryPatches.current);
    if (message) frame.current?.contentWindow?.postMessage(message, '*');
    lastSentState.current = state;
  }, [id, state]);

  return <iframe
    ref={frame}
    className={className}
    title={t('identity.interactiveMap')}
    src={`${frameOrigin}/native-map.html?id=${frameKey}&v=20261005-identity`}
    onLoad={() => {
      // Loading the document does not mean the asynchronous Google Maps API is
      // ready. Supply its initial state, but keep camera commands queued until
      // the frame explicitly reports tragenda-map-ready.
      frame.current?.contentWindow?.postMessage({ type: 'tragenda-map-appearance', mapId: id, theme: appearanceRef.current }, '*');
      frame.current?.contentWindow?.postMessage({ type: 'tragenda-map-state', mapId: id, state: stateRef.current }, '*');
      // onLoad may arrive after Google has already announced ready. Never
      // revoke readiness here; each ready handshake resends a full snapshot.
      lastSentState.current = stateRef.current;
    }}
    allow="geolocation"
  />;
}
